import React, { useState, useEffect } from 'react';
import { FileText, Download, X, Eye, Calendar, User, Printer } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Componente per visualizzare il PDF della visita (sola lettura per paziente)
 */
const VisitaPDFViewer = ({
  visitaId,
  patientName,
  visitaDate,
  onClose,
  canDownload = true
}) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPDF();
  }, [visitaId]);

  const loadPDF = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      // Use the correct endpoint with token as query param
      const pdfEndpoint = `${API}/api/referto/${visitaId}/pdf?token=${token}`;
      setPdfUrl(pdfEndpoint);
    } catch (err) {
      console.error('Errore caricamento PDF:', err);
      setError('Impossibile caricare il PDF della visita');
    } finally {
      setIsLoading(false);
    }
  };

  const downloadPDF = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `Visita_${patientName}_${visitaDate}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('PDF scaricato');
    }
  };

  const printPDF = () => {
    if (pdfUrl) {
      const printWindow = window.open(pdfUrl, '_blank');
      printWindow?.print();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-5xl h-[90vh] mx-4 flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-100">
              <FileText className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="font-bold text-gray-800">Referto Visita Oculistica</h2>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" /> {patientName}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> {visitaDate}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {canDownload && (
              <>
                <button
                  onClick={printPDF}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Stampa"
                >
                  <Printer className="w-5 h-5 text-gray-600" />
                </button>
                <button
                  onClick={downloadPDF}
                  className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 flex items-center gap-2 transition-colors"
                >
                  <Download className="w-4 h-4" /> Scarica PDF
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {isLoading ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">Caricamento PDF...</p>
              </div>
            </div>
          ) : error ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <X className="w-8 h-8 text-red-500" />
                </div>
                <p className="text-gray-800 font-medium mb-2">Errore</p>
                <p className="text-gray-500">{error}</p>
                <button
                  onClick={loadPDF}
                  className="mt-4 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600"
                >
                  Riprova
                </button>
              </div>
            </div>
          ) : pdfUrl ? (
            <iframe
              src={pdfUrl}
              className="w-full h-full border-0"
              title="PDF Visita"
            />
          ) : null}
        </div>

        {/* Footer - Solo lettura notice */}
        <div className="p-3 border-t bg-gray-50 text-center text-sm text-gray-500">
          <Eye className="w-4 h-4 inline mr-1" />
          Documento in sola lettura - Non modificabile
        </div>
      </div>
    </div>
  );
};

export default VisitaPDFViewer;
